import { create } from 'zustand';

type ErrorSeverity = 'error' | 'warning' | 'info' | 'success';

interface ErrorState {
  message: string | null;
  severity: ErrorSeverity;
  isOpen: boolean;
  showError: (message: string, severity?: ErrorSeverity) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  clearError: () => void;
}

/**
 * Global error store using Zustand
 *
 * Manages global error/success/info messages displayed in Snackbar
 *
 * @example
 * ```tsx
 * import { useErrorStore } from '@/stores/errorStore';
 *
 * function MyComponent() {
 *   const { showError, showSuccess } = useErrorStore();
 *
 *   const handleSubmit = async () => {
 *     try {
 *       await api.post('/data');
 *       showSuccess('儲存成功');
 *     } catch (error) {
 *       showError('儲存失敗');
 *     }
 *   };
 * }
 * ```
 */
export const useErrorStore = create<ErrorState>((set) => ({
  message: null,
  severity: 'error',
  isOpen: false,

  /**
   * Show error/warning/info/success message
   * @param message - Message to display
   * @param severity - Message severity (default: 'error')
   */
  showError: (message: string, severity: ErrorSeverity = 'error') => {
    set({
      message,
      severity,
      isOpen: true,
    });
  },

  /**
   * Show success message
   * @param message - Success message
   */
  showSuccess: (message: string) => {
    set({
      message,
      severity: 'success',
      isOpen: true,
    });
  },

  /**
   * Show warning message
   * @param message - Warning message
   */
  showWarning: (message: string) => {
    set({
      message,
      severity: 'warning',
      isOpen: true,
    });
  },

  /**
   * Show info message
   * @param message - Info message
   */
  showInfo: (message: string) => {
    set({
      message,
      severity: 'info',
      isOpen: true,
    });
  },

  /**
   * Clear/hide the error message
   */
  clearError: () => {
    set({
      isOpen: false,
    });
  },
}));
